import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import KubitSyncProgressCard from './KubitSyncProgressCard';

/**
 * Past sync runs list for the integration page. Pairs with the live
 * KubitSyncProgressCard above the activity log.
 *
 * Each row shows a one-line summary (status icon, started_at, duration,
 * trigger source). Clicking a row expands it inline to show the full
 * 8-step breakdown, reusing KubitSyncProgressCard in read-only mode
 * (no auto-collapse, no polling). Detail data is fetched on first expand
 * and cached in Redux by run_uuid, so re-expanding the same row is
 * instant.
 *
 * Empty state when no runs have ever been recorded for this company —
 * usually only on a brand-new tenant before the first sync.
 *
 * The component is presentational: parent wires Redux state + dispatch
 * via props. Same separation pattern as KubitSyncProgressCard.
 */

interface SyncRunSummary {
    run_uuid: string;
    status: 'running' | 'success' | 'partial' | 'error' | 'aborted';
    triggered_by: string;
    resource_filter: string | null;
    started_at: string | null;
    finished_at: string | null;
    total_duration_ms: number | null;
    error_message: string | null;
}

interface RunsListPayload {
    data: SyncRunSummary[];
    meta: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
    } | null;
}

interface Props {
    runs: RunsListPayload;
    runDetailCache: { [runUuid: string]: { run: any; steps: any[] } | undefined };
    onLoadPage: (page: number, perPage: number) => void;
    onLoadDetail: (runUuid: string) => void;
    perPage?: number;
}

const KubitSyncRunHistory: React.FC<Props> = ({
    runs,
    runDetailCache,
    onLoadPage,
    onLoadDetail,
    perPage = 10,
}) => {
    const intl = useIntl();
    const [expandedUuid, setExpandedUuid] = useState<string | null>(null);

    // Initial fetch on mount. Subsequent pages happen via the pagination
    // controls below.
    useEffect(() => {
        onLoadPage(1, perPage);
    }, []);

    function toggleExpand(runUuid: string) {
        if (expandedUuid === runUuid) {
            setExpandedUuid(null);
            return;
        }
        setExpandedUuid(runUuid);
        // Fetch detail only if not already cached.
        if (!runDetailCache[runUuid]) {
            onLoadDetail(runUuid);
        }
    }

    const rows = runs?.data || [];
    const meta = runs?.meta;

    if (rows.length === 0) {
        return (
            <div className="kubit-history-empty">
                <FormattedMessage
                    id="integrations.kubit.history.empty"
                    defaultMessage="No sync runs recorded yet."
                />
            </div>
        );
    }

    return (
        <div className="kubit-history">
            <ul className="kubit-history-list">
                {rows.map((run) => {
                    const isExpanded = expandedUuid === run.run_uuid;
                    const cached = runDetailCache[run.run_uuid];
                    return (
                        <li key={run.run_uuid} className={`kubit-history-row is-${run.status} ${isExpanded ? 'is-expanded' : ''}`}>
                            <button
                                type="button"
                                className="kubit-history-row-summary"
                                onClick={() => toggleExpand(run.run_uuid)}
                                aria-expanded={isExpanded}
                            >
                                <span className="kubit-history-icon" aria-hidden="true">
                                    {renderStatusIcon(run.status)}
                                </span>
                                <span className="kubit-history-time">
                                    {formatDateTime(run.started_at)}
                                </span>
                                <span className="kubit-history-duration">
                                    {run.total_duration_ms !== null
                                        ? formatDuration(run.total_duration_ms)
                                        : '—'}
                                </span>
                                <span className="kubit-history-trigger">
                                    <FormattedMessage
                                        id={`integrations.kubit.history.triggeredBy.${run.triggered_by}`}
                                        defaultMessage={run.triggered_by}
                                    />
                                </span>
                                <span className="kubit-history-status">
                                    <FormattedMessage
                                        id={`integrations.kubit.history.status.${run.status}`}
                                        defaultMessage={run.status}
                                    />
                                </span>
                                <span className="kubit-history-chevron" aria-hidden="true">
                                    {isExpanded ? '▾' : '▸'}
                                </span>
                            </button>

                            {isExpanded && (
                                <div className="kubit-history-detail">
                                    {cached ? (
                                        <KubitSyncProgressCard
                                            run={cached.run}
                                            steps={cached.steps}
                                            allowAutoCollapse={false}
                                        />
                                    ) : (
                                        <div className="kubit-history-loading">
                                            <FormattedMessage
                                                id="integrations.kubit.history.loadingDetail"
                                                defaultMessage="Loading details…"
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>

            {meta && meta.last_page > 1 && (
                <div className="kubit-history-pagination">
                    <button
                        type="button"
                        className="kubit-btn"
                        disabled={meta.current_page <= 1}
                        onClick={() => onLoadPage(meta.current_page - 1, perPage)}
                    >
                        <FormattedMessage
                            id="integrations.kubit.history.prev"
                            defaultMessage="Previous"
                        />
                    </button>
                    <span className="kubit-history-page-indicator">
                        <FormattedMessage
                            id="integrations.kubit.history.pageIndicator"
                            defaultMessage="Page {current} of {total}"
                            values={{
                                current: meta.current_page,
                                total: meta.last_page,
                            }}
                        />
                    </span>
                    <button
                        type="button"
                        className="kubit-btn"
                        disabled={meta.current_page >= meta.last_page}
                        onClick={() => onLoadPage(meta.current_page + 1, perPage)}
                    >
                        <FormattedMessage
                            id="integrations.kubit.history.next"
                            defaultMessage="Next"
                        />
                    </button>
                </div>
            )}
        </div>
    );
};

// ─── helpers ───────────────────────────────────────────────────────────────

function renderStatusIcon(status: string): React.ReactNode {
    switch (status) {
        case 'success':
            return <span className="kubit-icon-success">✓</span>;
        case 'error':
            return <span className="kubit-icon-error">⚠</span>;
        case 'partial':
            return <span className="kubit-icon-partial">!</span>;
        case 'aborted':
            return <span className="kubit-icon-skipped">—</span>;
        case 'running':
            return <span className="kubit-icon-running">⟳</span>;
        default:
            return <span>?</span>;
    }
}

function formatDateTime(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    // YYYY-MM-DD HH:mm — locale-agnostic; UI text is i18n'd via labels,
    // but the timestamp format should be unambiguous regardless of language.
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
}

function formatDuration(ms: number): string {
    if (ms < 1000) return `${ms} ms`;
    const seconds = ms / 1000;
    if (seconds < 60) return `${seconds.toFixed(1)} s`;
    const minutes = Math.floor(seconds / 60);
    const rem = Math.round(seconds % 60);
    return `${minutes}m ${rem}s`;
}

export default KubitSyncRunHistory;
